import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { PreventiveMaintenanceService } from '../../../../services/preventive-maintenance.service';
import { PreventiveMaintenance, PreventiveMaintenanceItemCreate, StockItem } from '../../../../models/PreventiveMaintenance';

@Component({
  selector: 'app-preventive-maintenance-details',
  templateUrl: './preventive-maintenance-details.component.html',
  styleUrls: ['./preventive-maintenance-details.component.css']
})
export class PreventiveMaintenanceDetailsComponent implements OnInit {
  preventiveMaintenance?: PreventiveMaintenance;
  loading = false;
  error: string | null = null;
  preventiveMaintenanceId!: number;
  
  // Modal de adicionar item
  showAddItemModal = false;
  itemForm!: FormGroup;
  stockItems: StockItem[] = [];
  isSubmittingItem = false;

  // Opções para selects
  units = ['UN', 'L', 'KG', 'M', 'M²', 'M³', 'CX', 'PC', 'RL', 'PR', 'DZ', 'CT', 'ML'];
  itemTypes = ['MaintenanceItem', 'Part', 'Fluid', 'Filter', 'Belt', 'Battery', 'Tire', 'Other'];

  constructor(
    private preventiveMaintenanceService: PreventiveMaintenanceService,
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder
  ) {
    this.initializeItemForm();
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.preventiveMaintenanceId = +params['id'];
        this.loadPreventiveMaintenance(this.preventiveMaintenanceId);
        this.loadStockItems();
      }
    });
  }

  initializeItemForm(): void {
    this.itemForm = this.fb.group({
      itemName: ['', [Validators.required, Validators.maxLength(200)]],
      description: ['', Validators.maxLength(500)],
      instructions: ['', Validators.maxLength(1000)],
      itemId: [''],
      quantity: [1, [Validators.required, Validators.min(0.01)]],
      unit: ['UN', Validators.required],
      itemType: ['MaintenanceItem', Validators.required],
      isRequired: [true],
      orderIndex: [0, [Validators.min(0)]],
      estimatedCost: ['', [Validators.min(0)]]
    });
  }

  loadStockItems(): void {
    this.preventiveMaintenanceService.getStockItems()
      .subscribe({
        next: (response) => {
          // Mapear campos PascalCase da API para camelCase do modelo
          const rawItems = Array.isArray(response) ? response : (response?.data || []);
          this.stockItems = rawItems.map((item: any) => ({
            id: item.Id,
            code: item.Code,
            name: item.Name,
            description: item.Description || undefined,
            type: item.Type || undefined,
            category: item.Category || undefined,
            quantity: item.CurrentQuantity || 0,
            currentQuantity: item.CurrentQuantity || 0,
            reservedQuantity: item.ReservedQuantity || 0,
            availableQuantity: item.AvailableQuantity || 0,
            unit: item.Unit || undefined,
            warehouseId: item.WarehouseId || undefined,
            warehouseName: item.WarehouseName || undefined,
            unitCost: item.UnitCost || undefined,
            totalValue: item.TotalValue || undefined,
            currencyCode: item.CurrencyCode || undefined,
            status: item.Status || undefined,
            isActive: item.IsActive || false
          }));
        },
        error: (err) => {
          console.error('Erro ao carregar itens do estoque:', err);
        }
      });
  }

  loadPreventiveMaintenance(id: number): void {
    this.loading = true;
    this.error = null;

    this.preventiveMaintenanceService.getById(id, true)
      .subscribe({
        next: (response) => {
          // Mapear campos PascalCase da API para camelCase do modelo
          const apiData = response.data;
          this.preventiveMaintenance = {
            id: apiData.Id,
            maintenanceName: apiData.MaintenanceName,
            description: apiData.Description || undefined,
            instructions: apiData.Instructions || undefined,
            vehicleType: apiData.VehicleType || undefined,
            vehicleBrand: apiData.VehicleBrand || undefined,
            vehicleModel: apiData.VehicleModel || undefined,
            targetMileage: apiData.IntervalMileage || undefined,
            estimatedCost: apiData.EstimatedCost || undefined,
            estimatedDurationHours: apiData.EstimatedMinutes ? apiData.EstimatedMinutes / 60 : undefined,
            isActive: apiData.IsActive,
            isRequired: apiData.IsRequired || false,
            requiresSignature: apiData.RequiresSignature || false,
            orderIndex: apiData.Priority || undefined,
            createdAt: apiData.CreatedAt || undefined,
            updatedAt: apiData.UpdatedAt || undefined,
            createdBy: apiData.CreatedBy || undefined,
            updatedBy: apiData.UpdatedBy || undefined,
            items: (apiData.Items || []).map((item: any) => ({
              id: item.Id,
              preventiveMaintenanceId: item.PreventiveMaintenanceId,
              itemName: item.ItemName,
              description: item.Description || undefined,
              instructions: item.Instructions || undefined,
              itemId: item.ItemId || undefined,
              quantity: item.Quantity || 0,
              unit: item.Unit || 'UN',
              itemType: item.ItemType || 'MaintenanceItem',
              isRequired: item.IsRequired || false,
              orderIndex: item.OrderIndex || undefined,
              estimatedCost: item.EstimatedCost || undefined,
              createdAt: item.CreatedAt || undefined,
              updatedAt: item.UpdatedAt || undefined,
              createdBy: item.CreatedBy || undefined,
              updatedBy: item.UpdatedBy || undefined
            }))
          };
          this.loading = false;
        },
        error: (err) => {
          this.error = err.error?.message || 'Erro ao carregar detalhes da preventiva';
          this.loading = false;
          console.error('Erro:', err);
        }
      });
  }

  calculateTotalItemsCost(): number {
    if (!this.preventiveMaintenance?.items) return 0;
    return this.preventiveMaintenance.items.reduce((total, item) => {
      return total + (item.estimatedCost || 0);
    }, 0);
  }

  editPreventiveMaintenance(): void {
    this.router.navigate(['/operations/preventive-maintenance', this.preventiveMaintenanceId, 'edit']);
  }

  deletePreventiveMaintenance(): void {
    if (confirm('Deseja realmente excluir esta preventiva?')) {
      this.preventiveMaintenanceService.delete(this.preventiveMaintenanceId)
        .subscribe({
          next: () => {
            this.router.navigate(['/operations/preventive-maintenance']);
          },
          error: (err) => {
            console.error('Erro ao excluir:', err);
            this.error = 'Erro ao excluir preventiva';
          }
        });
    }
  }

  goBack(): void {
    this.router.navigate(['/operations/preventive-maintenance']);
  }

  // ===== GERENCIAMENTO DE ITENS =====

  openAddItemModal(): void {
    this.showAddItemModal = true;
    this.itemForm.reset({
      itemName: '',
      description: '',
      instructions: '',
      itemId: '',
      quantity: 1,
      unit: 'UN',
      itemType: 'MaintenanceItem',
      isRequired: true,
      orderIndex: this.preventiveMaintenance?.items?.length || 0,
      estimatedCost: ''
    });
  }

  closeAddItemModal(): void {
    this.showAddItemModal = false;
    this.itemForm.reset();
  }

  onStockItemSelected(stockItemId: number): void {
    const stockItem = this.stockItems.find(item => item.id === stockItemId);
    if (stockItem) {
      this.itemForm.patchValue({
        itemName: stockItem.name,
        itemId: stockItem.id,
        unit: stockItem.unit || 'UN',
        description: stockItem.description || '',
        estimatedCost: stockItem.unitCost || ''
      });
    }
  }

  addItemToPreventiveMaintenance(): void {
    if (this.itemForm.invalid) {
      this.itemForm.markAllAsTouched();
      return;
    }

    this.isSubmittingItem = true;
    this.error = null;

    const itemData: PreventiveMaintenanceItemCreate = {
      itemName: this.itemForm.value.itemName,
      description: this.itemForm.value.description || undefined,
      instructions: this.itemForm.value.instructions || undefined,
      itemId: this.itemForm.value.itemId || undefined,
      quantity: this.itemForm.value.quantity,
      unit: this.itemForm.value.unit,
      itemType: this.itemForm.value.itemType,
      isRequired: this.itemForm.value.isRequired,
      orderIndex: this.itemForm.value.orderIndex,
      estimatedCost: this.itemForm.value.estimatedCost || undefined
    };

    this.preventiveMaintenanceService.addItem(this.preventiveMaintenanceId, itemData)
      .subscribe({
        next: () => {
          this.closeAddItemModal();
          // Recarregar a preventiva para mostrar o novo item
          this.loadPreventiveMaintenance(this.preventiveMaintenanceId);
          this.isSubmittingItem = false;
        },
        error: (err) => {
          console.error('Erro ao adicionar item:', err);
          this.error = err.error?.message || 'Erro ao adicionar item. Tente novamente.';
          this.isSubmittingItem = false;
        }
      });
  }
}

